// Кэш запросов TanStack Query. Ответы с кодом ошибки не повторяются сами: причину и шаг видит
// человек. Повтор — только при обрыве связи, один-два раза.
// Связь вернулась или вкладка снова на экране — данные экрана перечитываются фоновыми запросами
// (X-KP-Background: 1): их никто не просил, и сеанс они не продлевают (FR-008б). Отметка — у
// самого перечитывания: все страницы списков фоновые (T151), а действие человека в это время —
// обычный запрос (T123). Свои перезапросы TanStack при возврате связи и вкладки выключены: они
// идут вне inBackground — обычными запросами, и открытый экран продлевал бы сеанс при каждом
// возврате на вкладку (T118).
import { QueryClient } from '@tanstack/react-query';
import type { Query } from '@tanstack/react-query';
import { NetworkError, inBackground } from '../api/client.ts';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => error instanceof NetworkError && failureCount < 2,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/**
 * Перечитать данные открытого экрана. background — перезапрос без действия человека (связь
 * вернулась): запросы фоновые и сеанс не продлевают; «Повторить» в полосе «Нет связи» — действие
 * человека, запросы обычные.
 */
export function refetchActive(
  queryClient: QueryClient,
  { background }: { background: boolean },
): Promise<void> {
  // Запрос с meta.manualRefetch перечитывается только по команде человека (таблица протокола).
  const run = () =>
    queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => query.meta?.manualRefetch !== true,
    });
  return background ? inBackground(run) : run();
}

/**
 * Данные устарели к этой минуте — по staleTime запроса, как считает свой перезапрос TanStack.
 * Время — на момент проверки, а не по таймеру: в скрытой вкладке браузер придерживает таймеры.
 */
function isStaleNow(query: Query): boolean {
  return query.observers.some((observer) => {
    const { staleTime } = observer.options;
    return query.isStaleByTime(typeof staleTime === 'function' ? staleTime(query) : staleTime);
  });
}

/**
 * Вкладка снова на экране: перечитать устаревшие данные открытого экрана фоновыми запросами
 * (FR-008б, T118). Запрос, который уже идёт, не отменяется и не повторяется.
 */
export function refetchStale(queryClient: QueryClient): Promise<void> {
  return inBackground(() =>
    queryClient.refetchQueries({ type: 'active', predicate: isStaleNow }, { cancelRefetch: false }),
  );
}
